// Moved from `src/hooks/query/useQueryDocumentById.ts` (now a delegate) at
// D5, ARCFE-990. What crossed and what did not, each measured:
//   - the normalize (StreamingStatus seed + AbortController) builds IN
//     PACKAGE — `StreamingStatus` is the contracts' own D2a rider, and the
//     abort controller is part of the cached-entry shape the contracts type
//     declares.
//   - the BRAIN transform crosses the seam (`mapDocumentBrain`) — the app's
//     `mapNewBrain` composes free-user pricing/UI concerns a data package
//     may not read. Identity when unwired.
//   - `isFreeUser` is an INPUT — the delegate composes `useIsFreeUser`.
//   - the token crosses the seam hook (S2); `isValidObjectId` comes from
//     `@heidi/utils`.
//   - the module commands (`ensureQueryDocumentById`,
//     `invalidateDocumentQuery`) run on the seam queryClient; ensure passes
//     `isFreeUser` undefined exactly as the legacy ensure did.
import { isValidObjectId } from '@heidi/utils';
import { useQuery } from '@tanstack/react-query';
import {
  GET_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
  StreamingStatus,
} from '../../contracts';
import { getDocumentById } from '../api/documents';
import { getSessionDataConfig } from '../config';

type GetDocumentResponseLike = Omit<
  QueryDocumentResponse,
  'streamingStatus' | 'abortController'
>;

const normalizeDocumentData = (
  data: GetDocumentResponseLike,
  isFreeUser?: boolean
): QueryDocumentResponse => {
  const mapBrain = getSessionDataConfig().mapDocumentBrain ?? ((brain) => brain);
  return {
    ...data,
    brain: mapBrain(data.brain, isFreeUser) as QueryDocumentResponse['brain'],
    streamingStatus: StreamingStatus.NOT_STARTED,
    abortController: new AbortController(),
  };
};

export const invalidateDocumentQuery = async ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}) => {
  await getSessionDataConfig()
    .getQueryClient()
    .invalidateQueries({ queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }] });
};

export const ensureQueryDocumentById = async ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}) => {
  return await getSessionDataConfig()
    .getQueryClient()
    .ensureQueryData({
      queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      queryFn: async () => {
        const { data } = await getDocumentById(sessionId, documentId);
        if (data) {
          return normalizeDocumentData(data as GetDocumentResponseLike);
        }
      },
    });
};

export function useQueryDocumentById<T = QueryDocumentResponse>({
  sessionId,
  documentId,
  isFreeUser,
  select,
  refetchOnMount,
}: {
  sessionId: string;
  documentId: string;
  /** Composed app-side (`useIsFreeUser`) — feeds the injected brain transform. */
  isFreeUser?: boolean;
  select?: (data: QueryDocumentResponse | undefined) => T;
  refetchOnMount?: boolean | 'always';
}) {
  const token = getSessionDataConfig().useAuthToken?.();

  return useQuery({
    queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
    enabled: !!sessionId && !!token && isValidObjectId(documentId),
    select,
    refetchOnMount,
    queryFn: async () => {
      const { data } = await getDocumentById(sessionId, documentId);
      if (data) {
        return normalizeDocumentData(data as GetDocumentResponseLike, isFreeUser);
      }
    },
  });
}
